import { Link, useLocation, useRouter } from "@tanstack/react-router";
import {
  Factory,
  House,
  Import,
  ListChecks,
  LogOut,
  PanelLeft,
  PanelLeftClose,
  Settings,
  Truck,
} from "lucide-react";
import { useState } from "react";
import { UserAvatar } from "@/components/shared/user-avatar";
import { Button } from "@/components/ui/button";
import { signOut, useSession } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { useSidebar } from "./SidebarContext";

interface NavItem {
  label: string;
  icon: typeof House;
  href: string;
  disabled?: boolean;
}

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const { data: session } = useSession();
  const router = useRouter();
  const location = useLocation();
  const { isMobile, mobileOpen, setMobileOpen } = useSidebar();

  const navItems: NavItem[] = [
    { label: "Mass Update", icon: House, href: "/dashboard" },
    { label: "Sales Orders", icon: Truck, href: "/dashboard/search-sales-orders" },
    { label: "Work Orders", icon: Factory, href: "/dashboard/search-work-orders" },
    { label: "Import Work Orders", icon: Import, href: "/dashboard/import-work-orders" },
    { label: "Jobs", icon: ListChecks, href: "/dashboard/jobs" },
  ];
  const isAdmin = session?.user?.role === "admin";

  async function handleSignOut() {
    await signOut();
    router.navigate({ to: "/login" });
  }

  function handleNavClick() {
    if (isMobile) {
      setMobileOpen(false);
    }
  }

  // On mobile, don't show collapsed state — always show full sidebar
  const showLabels = isMobile ? true : !collapsed;

  const sidebarContent = (
    <aside
      className={cn(
        "flex flex-col border-r border-sidebar-border bg-sidebar transition-all duration-200",
        isMobile ? "w-64 h-full" : collapsed ? "w-16" : "w-64",
      )}
    >
      {/* Brand */}
      <div className="flex h-16 items-center justify-between border-b border-sidebar-border px-4">
        {showLabels && (
          <span className="font-display text-lg font-bold text-sidebar-foreground truncate">
            Mass Update
          </span>
        )}
        {!isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="text-sidebar-foreground shrink-0"
          >
            {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </Button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-2">
        {navItems.map((item) => {
          const isActive =
            item.href === "/dashboard"
              ? location.pathname === "/dashboard"
              : location.pathname.startsWith(item.href);

          return (
            <Link
              key={item.label}
              to={item.href}
              onClick={handleNavClick}
              className={cn(
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                isActive ? "bg-sidebar-accent text-sidebar-primary" : "text-sidebar-foreground/60",
                item.disabled && "pointer-events-none opacity-40",
                !item.disabled &&
                  !isActive &&
                  "hover:bg-sidebar-accent hover:text-sidebar-foreground",
              )}
            >
              <item.icon className="h-4 w-4 shrink-0" />
              {showLabels && <span>{item.label}</span>}
              {showLabels && item.disabled && (
                <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground">
                  Soon
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* User info + sign out */}
      <div className="border-t border-sidebar-border p-2">
        {session?.user && (
          <Link
            to="/dashboard/profile"
            onClick={handleNavClick}
            className={cn(
              "mb-2 flex items-center gap-3 rounded-md px-3 py-2 transition-colors hover:bg-sidebar-accent",
              !showLabels && "justify-center",
            )}
          >
            <UserAvatar user={session.user} className="h-8 w-8" />
            {showLabels && (
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-sidebar-foreground">
                  {session.user.name}
                </p>
                <p className="truncate text-xs text-muted-foreground">{session.user.email}</p>
              </div>
            )}
          </Link>
        )}
        <div className={cn("flex gap-2", !showLabels && "flex-col")}>
          {isAdmin ? (
            <Link
              to="/dashboard/settings"
              onClick={handleNavClick}
              className={cn(
                "flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                location.pathname.startsWith("/dashboard/settings")
                  ? "bg-sidebar-accent text-sidebar-primary"
                  : "text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground",
              )}
            >
              <Settings className="h-4 w-4 shrink-0" />
              {showLabels && <span>Settings</span>}
            </Link>
          ) : (
            <span
              title="Admin only"
              className="flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-sidebar-foreground/60 opacity-40"
            >
              <Settings className="h-4 w-4 shrink-0" />
              {showLabels && <span>Settings</span>}
            </span>
          )}
          <button
            type="button"
            onClick={handleSignOut}
            className={cn(
              "flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors",
            )}
          >
            <LogOut className="h-4 w-4 shrink-0" />
            {showLabels && <span>Sign Out</span>}
          </button>
        </div>
      </div>
    </aside>
  );

  // Mobile: overlay sidebar
  if (isMobile) {
    if (!mobileOpen) return null;

    return (
      <>
        {/* Backdrop */}
        <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setMobileOpen(false)} />
        {/* Slide-out sidebar */}
        <div className="fixed left-0 top-0 z-50 h-full">{sidebarContent}</div>
      </>
    );
  }

  // Desktop: normal sidebar
  return sidebarContent;
}
